import { getLayer } from './layerService.js';
import { capturePropertyState } from './nodeUtils.js';
import PropertyChangeCommand from './commands/PropertyChangeCommand.js';
import { useUndoStore } from './undoStore.js';

/**
 * Record ONE undo entry for a group of style writes that logically belong
 * together — the case App.vue's debounced property-change path cannot express,
 * because that window is time-based: it does guarantee a synchronous burst
 * lands in a single command, but it just as happily absorbs an UNRELATED edit
 * the user makes 200ms later into the same entry.
 *
 * This is the same mechanism App.vue already uses for its one non-debounced
 * property command (see the 'hass-config-changed' branch): capture the state
 * before, suppress layer history for the duration of the writes so the
 * debounced path never sees them at all, capture the state after, push one
 * PropertyChangeCommand. Nothing new — no batching layer, no transaction
 * stack; PropertyChangeCommand already carries a whole before/after snapshot,
 * so N writes and 1 write cost the same one entry.
 *
 * Use it for a multi-write edit whose intermediate states are meaningless on
 * their own (padding Overall → shorthand + four overrides removed; a side edit
 * that normalizes back into the shorthand). A single-property edit needs
 * nothing: the ordinary debounced path already handles it.
 *
 * @param node   the HaspObject being edited (needs id() + the style API)
 * @param apply  callback performing every write, synchronously
 * @param opts   { description, pageUuid } — both purely for the history panel
 * @returns the pushed PropertyChangeCommand, or null when nothing changed
 */
export function runPropertyTransaction(node, apply, { description, pageUuid = null } = {}) {
    if (!node || typeof apply !== 'function') return null;

    const prevState = capturePropertyState(node);
    const layer = getLayer();
    const wasSuppressed = layer?._historySuppress ?? false;
    if (layer) layer._historySuppress = true;
    try {
        apply();
    } finally {
        if (layer) layer._historySuppress = wasSuppressed;
    }
    const nextState = capturePropertyState(node);

    const command = new PropertyChangeCommand({
        description,
        objectUuid: node.id(),
        pageUuid,
        prevState,
        nextState,
    });
    // A no-op edit (typing the value that is already effective) must not add an
    // undo entry the user then has to step back through.
    if (command.changedProperties.length === 0) return null;
    useUndoStore().push(command);
    return command;
}
